import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Search, 
  Loader2,
  AlertTriangle,
  MapPin,
  Phone,
  User,
  Calendar,
  X,
  ZoomIn,
  ZoomOut,
  Crosshair,
  Filter,
  List,
  ChevronDown,
  ExternalLink,
  RefreshCw,
  Truck,
  Route,
  MessageSquare
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import RoutePlanner from './RoutePlanner';
import SendSMSModal from './SendSMSModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { safeFetch } from '../../utils/safeFetch';
// Try to get API key from env, will also try to fetch from backend
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

// Internal status colors (exact hex from requirements)
const INTERNAL_STATUS_COLORS = {
  new_booking: '#F97316',      // Bright orange - attention needed
  booking_reviewed: '#FBBF24', // Amber/yellow - reviewed but needs action
  confirmed: '#3B82F6',
  driver_assigned: '#6366F1',
  on_site: '#F59E0B',
  collection_in_progress: '#FB923C',
  collected: '#22C55E',
  internal_disposal_pending: '#A855F7',
  disposed: '#14B8A6',
  completed: '#15803D',
  on_hold: '#9CA3AF',
  partial_collection: '#F97316',
  failed_collection: '#EF4444',
  cancelled: '#6B7280',
};

const ATTENTION_STATUSES = ['on_hold', 'failed_collection', 'partial_collection'];
const DEFAULT_STATUSES = ['new_booking', 'booking_reviewed', 'confirmed', 'driver_assigned'];

// Helper to get the best available address from a booking
const getBookingAddress = (booking) => {
  return booking?.waste_details?.site_address 
    || booking?.delivery_details?.delivery_address 
    || booking?.delivery_details?.pickup_address 
    || '';
};

// Default center (Melbourne)
const DEFAULT_CENTER = { lat: -37.8136, lng: 144.9631 };
const DEFAULT_ZOOM = 10;

const MapDispatch = () => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const markerClustererRef = useRef(null);
  const infoWindowRef = useRef(null);
  
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [statusOptions, setStatusOptions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [apiKey, setApiKey] = useState(GOOGLE_MAPS_API_KEY);
  const [isGeocoding, setIsGeocoding] = useState(false);
  
  // Helper to safely parse response - prevents "body stream already read" error
  const safeParseResponse = async (response) => {
    try {
      // Clone the response immediately to avoid body stream issues
      const clone = response.clone();
      const text = await clone.text();
      try {
        return JSON.parse(text);
      } catch {
        return { detail: text || 'Server error' };
      }
    } catch (cloneError) {
      // If cloning fails, the body was already read - return a generic error
      console.error('Response clone failed:', cloneError);
      return { detail: 'Request failed - please try again' };
    }
  };
  
  // Filters
  const [statusFilters, setStatusFilters] = useState([]);
  const [todayOnly, setTodayOnly] = useState(false);
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [showList, setShowList] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [showNeedsLocation, setShowNeedsLocation] = useState(false);

  // Driver assignment
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [assigningDriver, setAssigningDriver] = useState(false);

  // Route Planner
  const [routeMode, setRouteMode] = useState(false);
  const [routeStops, setRouteStops] = useState([]);
  const routeModeRef = useRef(false);
  const routeStopsRef = useRef([]);

  // SMS modal
  const [smsModalOpen, setSmsModalOpen] = useState(false);

  // Performance: exclude "completed" bookings from initial fetch (they're rarely needed on dispatch)
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [statusCounts, setStatusCounts] = useState({});
  const [loadedStatuses, setLoadedStatuses] = useState([]);
  const loadedStatusesRef = useRef([]);

  // Fetch API key from backend if not available
  useEffect(() => {
    const fetchApiKey = async () => {
      if (apiKey) return;
      try {
        const result = await safeFetch(`${API_URL}/api/config/maps-key`);
        if (result.ok && result.data?.key) {
          setApiKey(result.data.key);
        }
      } catch (err) {
        console.error('Failed to fetch maps API key:', err);
      }
    };
    fetchApiKey();
  }, [apiKey]);

  // Load Google Maps script
  useEffect(() => {
    if (!apiKey) {
      setMapError('Google Maps API key not configured');
      return;
    }
    
    // Set up global error handler for Google Maps authentication failures
    window.gm_authFailure = () => {
      console.error('Google Maps authentication failed');
      setMapError(
        'Google Maps authentication failed. The API key may not be authorized for this domain. ' +
        'Please check your Google Cloud Console → APIs & Services → Credentials and ensure this domain is allowed.'
      );
    };
    
    // CRITICAL: Restore the ORIGINAL window.fetch for Google Maps tile loading.
    // The Emergent platform's emergent-main.js patches window.fetch AND re-patches it
    // on a timer, consuming response bodies before Google Maps can read tile data.
    // We saved the original fetch in index.html before emergent-main.js loaded.
    // We must LOCK it with Object.defineProperty to prevent re-patching.
    if (window.__cleanFetch && !window._mapFetchRestored) {
      try {
        Object.defineProperty(window, 'fetch', {
          value: window.__cleanFetch,
          writable: false,
          configurable: false
        });
        window._mapFetchRestored = true;
        console.log('Locked original fetch for Google Maps tile loading');
      } catch (e) {
        // Fallback: keep re-applying the clean fetch
        window.fetch = window.__cleanFetch;
        window._mapFetchRestored = true;
        const interval = setInterval(() => {
          try { window.fetch = window.__cleanFetch; } catch (ignored) {}
        }, 100);
        setTimeout(() => clearInterval(interval), 30000);
        console.log('Using interval-based fetch restoration for Google Maps');
      }
    }
    
    // If Google Maps is already fully loaded, use it immediately
    if (window.google && window.google.maps && window.google.maps.Map) {
      console.log('Google Maps already available');
      setMapLoaded(true);
      setMapError(null);
      return;
    }

    // Poll for Google Maps to become available (handles both existing and new script loads)
    const checkInterval = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.Map) {
        clearInterval(checkInterval);
        console.log('Google Maps is now available');
        setMapLoaded(true);
        setMapError(null);
      }
    }, 200);

    // If no script exists yet, load it ourselves
    const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=marker&loading=async&v=weekly`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        clearInterval(checkInterval);
        setMapError('Failed to load Google Maps. Please check the API key and ensure billing is enabled.');
      };
      document.head.appendChild(script);
    }

    // Timeout after 15 seconds
    const timeout = setTimeout(() => {
      clearInterval(checkInterval);
      if (!window.google || !window.google.maps) {
        setMapError('Google Maps timed out loading. Please refresh the page or check the API key configuration.');
      }
    }, 15000);
    
    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, [apiKey]);

  // Initialize map once loaded
  useEffect(() => {
    if (!mapLoaded || mapInstanceRef.current) return;

    const initMap = () => {
      if (!mapRef.current) return false;
      try {
        const mapOptions = {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        };

        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, mapOptions);
        infoWindowRef.current = new window.google.maps.InfoWindow();
        console.log('Google Map instance created successfully');
        
        // Listen for tile load
        let tilesLoaded = false;
        window.google.maps.event.addListenerOnce(mapInstanceRef.current, 'tilesloaded', () => {
          tilesLoaded = true;
          console.log('Map tiles loaded successfully');
          setMapError(null);
        });
        
        // Recovery attempt after 10 seconds if tiles haven't loaded
        setTimeout(() => {
          if (!tilesLoaded && mapInstanceRef.current) {
            console.warn('Map tiles may not have loaded - attempting recovery');
            window.google.maps.event.trigger(mapInstanceRef.current, 'resize');
            mapInstanceRef.current.setCenter(DEFAULT_CENTER);
            mapInstanceRef.current.setZoom(DEFAULT_ZOOM);
            
            setTimeout(() => {
              if (!tilesLoaded) {
                setMapError(
                  'Map tiles failed to load. This is usually caused by the Google Maps API key not being authorized for this domain. ' +
                  'Please go to Google Cloud Console → APIs & Services → Credentials → your API key → Application restrictions → ' +
                  'Add your website domain (e.g., https://transa.com.au/*) to the HTTP referrers list.'
                );
              }
            }, 5000);
          }
        }, 10000);
        
        return true;
      } catch (err) {
        console.error('Map initialization error:', err);
        setMapError('Failed to initialize Google Maps: ' + err.message);
        return true; // Don't retry on error
      }
    };

    // Try immediately, then retry every 200ms until the ref is ready (up to 5 seconds)
    if (initMap()) return;
    
    let attempts = 0;
    const retryInterval = setInterval(() => {
      attempts++;
      if (initMap() || attempts > 25) {
        clearInterval(retryInterval);
        if (attempts > 25 && !mapInstanceRef.current) {
          console.error('Map init failed: mapRef never became available after 5 seconds');
        }
      }
    }, 200);
    
    return () => clearInterval(retryInterval);
  }, [mapLoaded]);

  // Fetch data
  useEffect(() => {
    fetchStatusOptions();
    fetchStatusCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load booking data on-demand for whichever statuses are selected.
  // By default only the 4 active-dispatch statuses load, keeping the map fast.
  // Heavy historical statuses (e.g. Collected) load only when their box is ticked.
  useEffect(() => {
    ensureStatusesLoaded(statusFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilters]);

  // Compute base-filtered bookings (everything except status filter) for accurate counts
  const baseFilteredBookings = useMemo(() => {
    let filtered = [...bookings];
    
    if (todayOnly) {
      const melbourneNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }));
      const today = melbourneNow.getFullYear() + '-' + 
        String(melbourneNow.getMonth() + 1).padStart(2, '0') + '-' + 
        String(melbourneNow.getDate()).padStart(2, '0');
      filtered = filtered.filter(b => b.scheduled_date === today || b.created_at?.startsWith(today));
    }
    
    if (attentionOnly) {
      filtered = filtered.filter(b => ATTENTION_STATUSES.includes(b.internal_status));
    }
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(b => 
        getBookingAddress(b)?.toLowerCase().includes(search) ||
        b.client_name?.toLowerCase().includes(search) ||
        b.booking_ref?.toLowerCase().includes(search)
      );
    }
    
    if (!showNeedsLocation) {
      filtered = filtered.filter(b => b.lat && b.lng);
    }
    
    return filtered;
  }, [bookings, todayOnly, attentionOnly, searchTerm, showNeedsLocation]);

  // Apply status filter on top of base-filtered bookings
  useEffect(() => {
    let filtered = [...baseFilteredBookings];
    
    if (statusFilters.length > 0) {
      filtered = filtered.filter(b => statusFilters.includes(b.internal_status));
    }
    
    setFilteredBookings(filtered);
  }, [baseFilteredBookings, statusFilters]);

  // Update markers when filtered bookings change
  useEffect(() => {
    if (!mapInstanceRef.current || !mapLoaded) return;
    
    updateMarkers();
  }, [filteredBookings, mapLoaded]);

  const fetchStatusOptions = async () => {
    try {
      const result = await safeFetch(`${API_URL}/api/v2/bookings/statuses`);
      if (result.ok) {
        setStatusOptions(result.data);
        setStatusFilters(DEFAULT_STATUSES);
      }
    } catch (err) {
      console.error('Failed to fetch status options:', err);
    }
  };

  const fetchStatusCounts = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/v2/bookings/status-counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (result.ok) setStatusCounts(result.data.counts || {});
    } catch (err) {
      console.error('Failed to fetch status counts:', err);
    }
  };

  // Fetch (and merge) booking documents for a specific set of internal statuses.
  // Replaces any previously loaded docs of those statuses with fresh data.
  const fetchBookingsForStatuses = useCallback(async (statuses) => {
    const list = (statuses || []).filter(Boolean);
    if (list.length === 0) return;
    try {
      setIsLoading(true);
      const token = localStorage.getItem('admin_token');
      const statusParam = `&internal_status=${encodeURIComponent(list.join(','))}`;
      let acc = [];
      let page = 1;
      const pageSize = 500;
      let hasMore = true;

      while (hasMore) {
        const result = await safeFetch(`${API_URL}/api/v2/bookings/?page=${page}&page_size=${pageSize}${statusParam}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!result.ok) throw new Error('Failed to fetch bookings');
        const data = result.data;
        const batch = data.bookings || (Array.isArray(data) ? data : []);
        acc = acc.concat(batch);
        const totalPages = data.total_pages || 1;
        hasMore = page < totalPages;
        page++;
      }

      setBookings(prev => {
        const others = prev.filter(b => !list.includes(b.internal_status));
        return [...others, ...acc];
      });
      setLoadedStatuses(prev => {
        const next = Array.from(new Set([...prev, ...list]));
        loadedStatusesRef.current = next;
        return next;
      });
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load only statuses that haven't been fetched yet (keeps toggling instant).
  const ensureStatusesLoaded = useCallback((statuses) => {
    const missing = (statuses || []).filter(s => !loadedStatusesRef.current.includes(s));
    if (missing.length > 0) fetchBookingsForStatuses(missing);
  }, [fetchBookingsForStatuses]);

  // Re-fetch everything currently loaded (used after a booking mutation) + refresh counts.
  const refreshLoaded = useCallback(async () => {
    const loaded = loadedStatusesRef.current;
    if (loaded.length > 0) {
      await fetchBookingsForStatuses(loaded);
    }
    fetchStatusCounts();
  }, [fetchBookingsForStatuses]);

  // Count for a status: dynamic (respects Today/Search) if loaded, else the
  // lightweight global geocoded count from the status-counts endpoint.
  const getStatusCount = (key) => {
    if (loadedStatuses.includes(key)) {
      return baseFilteredBookings.filter(b => b.internal_status === key).length;
    }
    return statusCounts[key] || 0;
  };

  const geocodeBooking = async (bookingId, address) => {
    if (!address) {
      alert('No address to geocode');
      return;
    }
    
    setIsGeocoding(true);
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/geocode`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          address: address,
          booking_id: bookingId
        })
      });
      
      if (!result.ok) {
        throw new Error(result.data?.detail || 'Failed to geocode');
      }
      
      alert(`Location updated! Lat: ${result.data.lat.toFixed(6)}, Lng: ${result.data.lng.toFixed(6)}`);
      
      // Refresh bookings to show on map
      await refreshLoaded();
      
      // Update selected booking if it was the one geocoded
      if (selectedBooking?.id === bookingId) {
        setSelectedBooking({
          ...selectedBooking,
          lat: result.data.lat,
          lng: result.data.lng,
          needs_location_fix: false
        });
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setIsGeocoding(false);
    }
  };

  // Fetch available drivers for assignment
  const fetchDrivers = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/admin/drivers/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (result.ok) setAvailableDrivers(result.data.drivers || []);
    } catch (err) { /* silent */ }
  }, []);

  useEffect(() => { fetchDrivers(); }, [fetchDrivers]);

  // Route planner helpers
  const toggleRouteMode = () => {
    const next = !routeMode;
    setRouteMode(next);
    routeModeRef.current = next;
    if (!next) {
      setRouteStops([]);
      routeStopsRef.current = [];
    }
    setSelectedBooking(null);
  };
  const removeRouteStop = (id) => {
    const newStops = routeStops.filter(s => s.id !== id);
    setRouteStops(newStops);
    routeStopsRef.current = newStops;
  };
  const reorderRouteStops = (newStops) => {
    setRouteStops(newStops);
    routeStopsRef.current = newStops;
  };
  const clearRouteStops = () => {
    setRouteStops([]);
    routeStopsRef.current = [];
  };


  const assignDriver = async (bookingId, driverId) => {
    setAssigningDriver(true);
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/admin/drivers/assign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: bookingId, driver_id: driverId || null })
      });
      if (result.ok) {
        await refreshLoaded();
        const driver = availableDrivers.find(d => d.id === driverId);
        if (selectedBooking?.id === bookingId) {
          setSelectedBooking(prev => ({
            ...prev,
            assigned_driver_id: driverId || null,
            assigned_driver_name: driver?.name || null,
            internal_status: driverId && ['new_booking', 'confirmed'].includes(prev.internal_status) ? 'driver_assigned' : prev.internal_status
          }));
        }
        fetchDrivers();
      }
    } catch (err) {
      console.error('Failed to assign driver:', err);
    } finally {
      setAssigningDriver(false);
    }
  };

  const updateMarkers = () => {
    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Create new markers
    filteredBookings.forEach(booking => {
      if (!booking.lat || !booking.lng) return;

      const color = INTERNAL_STATUS_COLORS[booking.internal_status] || '#E5E7EB';
      
      // Create custom marker icon as SVG
      const svgMarker = {
        path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
        fillColor: color,
        fillOpacity: 1,
        strokeWeight: 2,
        strokeColor: '#FFFFFF',
        scale: 1.5,
        anchor: new window.google.maps.Point(12, 22),
      };

      const marker = new window.google.maps.Marker({
        position: { lat: booking.lat, lng: booking.lng },
        map: mapInstanceRef.current,
        icon: svgMarker,
        title: booking.booking_ref,
      });

      marker.addListener('click', () => {
        if (routeModeRef.current) {
          // In route mode: toggle stop
          const currentStops = routeStopsRef.current;
          const exists = currentStops.find(s => s.id === booking.id);
          let newStops;
          if (exists) {
            newStops = currentStops.filter(s => s.id !== booking.id);
          } else {
            newStops = [...currentStops, booking];
          }
          routeStopsRef.current = newStops;
          setRouteStops(newStops);
        } else {
          setSelectedBooking(booking);
        }
        mapInstanceRef.current.panTo({ lat: booking.lat, lng: booking.lng });
      });

      markersRef.current.push(marker);
    });

    // Fit bounds if we have markers
    if (markersRef.current.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      markersRef.current.forEach(marker => bounds.extend(marker.getPosition()));
      
      // Only fit bounds if we have multiple markers spread out
      if (markersRef.current.length > 1) {
        mapInstanceRef.current.fitBounds(bounds, { padding: 50 });
      }
    }
  };

  const handleZoomIn = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setZoom(mapInstanceRef.current.getZoom() + 1);
    }
  };

  const handleZoomOut = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setZoom(mapInstanceRef.current.getZoom() - 1);
    }
  };

  const handleRecenter = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setCenter(DEFAULT_CENTER);
      mapInstanceRef.current.setZoom(DEFAULT_ZOOM);
    }
  };

  const focusOnBooking = (booking) => {
    if (routeMode) {
      // In route mode: toggle stop
      const exists = routeStops.find(s => s.id === booking.id);
      let newStops;
      if (exists) {
        newStops = routeStops.filter(s => s.id !== booking.id);
      } else {
        newStops = [...routeStops, booking];
      }
      setRouteStops(newStops);
      routeStopsRef.current = newStops;
    } else {
      setSelectedBooking(booking);
    }
    if (mapInstanceRef.current && booking.lat && booking.lng) {
      mapInstanceRef.current.panTo({ lat: booking.lat, lng: booking.lng });
      mapInstanceRef.current.setZoom(15);
    }
  };

  const toggleStatusFilter = (status) => {
    setStatusFilters(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const updateInternalStatus = async (bookingId, newStatus) => {
    const requiresNote = statusOptions?.statuses_requiring_note?.includes(newStatus);
    if (requiresNote && !statusNote.trim()) {
      alert(`A note is required when setting status to: ${statusOptions?.internal_statuses?.[newStatus]?.label || newStatus}`);
      return;
    }
    
    setIsUpdating(true);
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/v2/bookings/${bookingId}/internal-status`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          internal_status: newStatus,
          note: statusNote || null
        })
      });

      if (!result.ok) {
        throw new Error(result.data?.detail || 'Failed to update status');
      }
      
      setSelectedBooking(result.data);
      setStatusNote('');
      
      // Refresh bookings to update markers
      await refreshLoaded();
    } catch (err) {
      alert(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Australia/Melbourne'
    });
  };

  const getStatusBadge = (status, colors, options) => {
    const color = colors[status] || '#E5E7EB';
    const label = options?.[status]?.label || status?.replace(/_/g, ' ');
    const isLight = ['#E5E7EB', '#CBD5E1', '#FACC15'].includes(color);
    
    return (
      <span 
        className="px-2 py-1 rounded text-xs font-medium whitespace-nowrap"
        style={{ 
          backgroundColor: color,
          color: isLight ? '#374151' : '#FFFFFF'
        }}
      >
        {label}
      </span>
    );
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">Map Dispatch</h1>
          <p className="text-[#64748B] text-sm">
            {filteredBookings.length} jobs with location • {bookings.filter(b => !b.lat || !b.lng).length} need location fix
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className={showFilters ? 'bg-[#F0FDFA] border-[#0D9488]' : 'border-[#E2E8F0]'}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="w-4 h-4 mr-1" />
            Filters
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={showList ? 'bg-[#F0FDFA] border-[#0D9488]' : 'border-[#E2E8F0]'}
            onClick={() => setShowList(!showList)}
          >
            <List className="w-4 h-4 mr-1" />
            List
          </Button>
          <Button
            size="sm"
            className={routeMode
              ? 'bg-[#6366F1] hover:bg-[#4F46E5] text-white'
              : 'bg-[#0D9488] hover:bg-[#0F766E] text-white'}
            onClick={toggleRouteMode}
            data-testid="toggle-route-planner"
          >
            <Route className="w-4 h-4 mr-1" />
            {routeMode ? `Planning (${routeStops.length})` : 'Plan Route'}
          </Button>
        </div>
      </div>

      <div className="flex-1 flex gap-4 min-h-0">
        {/* Sidebar Filters */}
        {showFilters && (
          <Card className="w-64 flex-shrink-0 overflow-y-auto">
            <CardContent className="p-4 space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 text-sm"
                />
              </div>

              {/* Quick Toggles */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={todayOnly}
                    onChange={(e) => setTodayOnly(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-[#0D9488] focus:ring-[#0D9488]"
                  />
                  <span className="text-sm">Today Only</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={attentionOnly}
                    onChange={(e) => setAttentionOnly(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-sm text-orange-600 font-medium">Attention Needed</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showNeedsLocation}
                    onChange={(e) => setShowNeedsLocation(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-red-500 focus:ring-red-500"
                  />
                  <span className="text-sm text-red-600 font-medium">
                    Needs Location ({baseFilteredBookings.filter(b => !b.lat || !b.lng).length})
                  </span>
                </label>
                <label
                  className="flex items-center gap-2 cursor-pointer pt-1 mt-1 border-t border-[#E2E8F0]"
                  title="Completed bookings are excluded by default to speed up map loading"
                  data-testid="include-completed-toggle"
                >
                  <input
                    type="checkbox"
                    checked={includeCompleted}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setIncludeCompleted(on);
                      setStatusFilters(prev => on
                        ? Array.from(new Set([...prev, 'completed']))
                        : prev.filter(s => s !== 'completed'));
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-[#0D9488] focus:ring-[#0D9488]"
                    data-testid="include-completed-checkbox"
                  />
                  <span className="text-sm text-[#64748B]">
                    Include Completed {includeCompleted && '🔄'}
                  </span>
                </label>
              </div>

              {/* Status Checkboxes */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-[#64748B] uppercase">Status Filter</h3>
                  <div className="flex gap-2">
                    <button 
                      className="text-xs text-[#0D9488] hover:underline"
                      onClick={() => setStatusFilters(Object.keys(statusOptions?.internal_statuses || {}))}
                    >
                      All
                    </button>
                    <button 
                      className="text-xs text-[#64748B] hover:underline"
                      onClick={() => setStatusFilters([])}
                    >
                      None
                    </button>
                  </div>
                </div>
                <div className="space-y-1 max-h-80 overflow-y-auto">
                  {statusOptions?.internal_statuses && Object.entries(statusOptions.internal_statuses).map(([key, value]) => {
                    const count = getStatusCount(key);
                    return (
                      <label key={key} className="flex items-center gap-2 cursor-pointer py-1">
                        <input
                          type="checkbox"
                          checked={statusFilters.includes(key)}
                          onChange={() => toggleStatusFilter(key)}
                          className="w-3 h-3 rounded border-gray-300"
                          style={{ accentColor: value.color }}
                        />
                        <span 
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: value.color }}
                        />
                        <span className="text-xs">{value.label} ({count})</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Legend */}
              <div className="border-t pt-4">
                <h3 className="text-xs font-semibold text-[#64748B] uppercase mb-2">Legend</h3>
                <div className="space-y-1">
                  {statusOptions?.internal_statuses && Object.entries(statusOptions.internal_statuses).map(([key, value]) => {
                    const count = getStatusCount(key);
                    return count > 0 ? (
                      <div key={key} className="flex items-center gap-2">
                        <span 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: value.color }}
                        />
                        <span className="text-xs text-[#64748B]">{value.label} ({count})</span>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Map */}
          <Card className="flex-1 relative overflow-hidden">
            {mapError ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 p-8 text-center">
                <AlertTriangle className="w-12 h-12 text-orange-500 mb-4" />
                <p className="text-[#111111] font-medium mb-2">Map Loading Issue</p>
                <p className="text-[#64748B] text-sm mb-4 max-w-lg">{mapError}</p>
                <div className="flex gap-3 mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setMapError(null);
                      mapInstanceRef.current = null;
                      setMapLoaded(false);
                      // Remove existing script and reload
                      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
                      if (existingScript) existingScript.remove();
                      delete window.google;
                      // Trigger re-mount
                      setTimeout(() => {
                        const script = document.createElement('script');
                        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=marker&v=weekly`;
                        script.async = true;
                        document.head.appendChild(script);
                      }, 500);
                    }}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry Loading Map
                  </Button>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200 text-left max-w-md">
                  <p className="text-xs font-semibold text-[#111111] mb-2">To fix this:</p>
                  <ol className="text-xs text-[#64748B] space-y-1 list-decimal pl-4">
                    <li>Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-[#0D9488] underline">Google Cloud Console → Credentials</a></li>
                    <li>Find your Maps API key and click Edit</li>
                    <li>Under "Application restrictions" → "HTTP referrers"</li>
                    <li>Add: <code className="bg-gray-100 px-1 rounded">https://transa.com.au/*</code></li>
                    <li>Also add: <code className="bg-gray-100 px-1 rounded">https://www.transa.com.au/*</code></li>
                    <li>Ensure "Maps JavaScript API" is enabled under API restrictions</li>
                  </ol>
                </div>
                <p className="text-[#94A3B8] text-xs mt-4">
                  You can still view and manage bookings in the list below.
                </p>
              </div>
            ) : !mapLoaded || isLoading ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100">
                <Loader2 className="w-8 h-8 animate-spin text-[#0D9488] mb-2" />
                <p className="text-[#64748B] text-sm">Loading map...</p>
              </div>
            ) : (
              <>
                <div ref={mapRef} className="w-full h-full" />
                
                {/* Map Controls */}
                <div className="absolute top-4 right-4 flex flex-col gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="bg-white shadow-md hover:bg-gray-50"
                    onClick={handleZoomIn}
                  >
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="bg-white shadow-md hover:bg-gray-50"
                    onClick={handleZoomOut}
                  >
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="bg-white shadow-md hover:bg-gray-50"
                    onClick={handleRecenter}
                  >
                    <Crosshair className="w-4 h-4" />
                  </Button>
                </div>
              </>
            )}
          </Card>

          {/* Booking List Below Map */}
          {showList && (
            <Card className="mt-4 max-h-48 overflow-y-auto">
              <div className="divide-y divide-[#E2E8F0]">
                {filteredBookings.length === 0 ? (
                  <div className="p-4 text-center text-[#64748B] text-sm">
                    {showNeedsLocation 
                      ? 'All bookings have valid locations!' 
                      : 'No bookings match the current filters'}
                  </div>
                ) : (
                  filteredBookings.map(booking => (
                    <div
                      key={booking.id}
                      className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-[#F8FAFC] transition-colors ${
                        routeMode && routeStops.find(s => s.id === booking.id) ? 'bg-[#EDE9FE] border-l-4 border-[#6366F1]' :
                        selectedBooking?.id === booking.id ? 'bg-[#F0FDFA]' : ''
                      }`}
                      onClick={() => focusOnBooking(booking)}
                    >
                      <span 
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: INTERNAL_STATUS_COLORS[booking.internal_status] || '#E5E7EB' }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#111111] truncate">
                          {booking.booking_ref}
                          {(!booking.lat || !booking.lng) && (
                            <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-red-100 text-red-600 rounded">
                              No Location
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-[#64748B] truncate">
                          {getBookingAddress(booking) || 'No address'}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0 flex items-center gap-2">
                        {(!booking.lat || !booking.lng) && getBookingAddress(booking) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs border-red-300 text-red-600 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              geocodeBooking(booking.id, getBookingAddress(booking));
                            }}
                            disabled={isGeocoding}
                            data-testid={`fix-location-${booking.id}`}
                          >
                            {isGeocoding ? <Loader2 className="w-3 h-3 animate-spin" /> : <MapPin className="w-3 h-3" />}
                            <span className="ml-1">Fix</span>
                          </Button>
                        )}
                        <div>
                          <p className="text-xs text-[#64748B]">{booking.client_name}</p>
                          {booking.assigned_driver_name && (
                            <p className="text-xs text-[#0D9488] flex items-center gap-0.5"><Truck className="w-3 h-3" />{booking.assigned_driver_name}</p>
                          )}
                          {booking.scheduled_date && (
                            <p className="text-xs text-[#94A3B8]">{booking.scheduled_date}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Booking Detail Drawer */}
        {selectedBooking && (
          <Card className="w-96 flex-shrink-0 overflow-y-auto">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-[#111111]">{selectedBooking.booking_ref}</h3>
                <button 
                  onClick={() => setSelectedBooking(null)}
                  className="text-[#94A3B8] hover:text-[#111111]"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Status Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                {getStatusBadge(selectedBooking.internal_status, INTERNAL_STATUS_COLORS, statusOptions?.internal_statuses)}
                {getStatusBadge(selectedBooking.external_status, { ...INTERNAL_STATUS_COLORS, booking_received: '#E5E7EB', scheduled: '#3B82F6', on_the_way: '#F59E0B', collected: '#22C55E', completed: '#15803D', cancelled: '#6B7280' }, statusOptions?.external_statuses)}
                {ATTENTION_STATUSES.includes(selectedBooking.internal_status) && (
                  <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Attention
                  </span>
                )}
              </div>

              {/* Location Warning */}
              {(!selectedBooking.lat || !selectedBooking.lng) && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-red-700 text-sm font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Location Missing
                  </p>
                  <p className="text-red-600 text-xs mt-1">
                    This booking doesn't have coordinates and won't appear on the map.
                  </p>
                  {getBookingAddress(selectedBooking) && (
                    <Button
                      size="sm"
                      className="mt-2 bg-red-600 hover:bg-red-700 text-white"
                      onClick={() => geocodeBooking(selectedBooking.id, getBookingAddress(selectedBooking))}
                      disabled={isGeocoding}
                      data-testid="fix-location-now-btn"
                    >
                      {isGeocoding ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Geocoding...</>
                      ) : (
                        <><MapPin className="w-4 h-4 mr-2" /> Fix Location Now</>
                      )}
                    </Button>
                  )}
                </div>
              )}

              {/* Finance Badges */}
              <div className="flex gap-2 mb-4">
                {selectedBooking.invoiced && (
                  <span className="px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: '#FACC15', color: '#374151' }}>
                    Invoiced
                  </span>
                )}
                {selectedBooking.paid && (
                  <span className="px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: '#16A34A', color: '#FFFFFF' }}>
                    Paid
                  </span>
                )}
              </div>

              {/* Details */}
              <div className="space-y-3 text-sm">
                <div className="bg-[#F8FAFC] rounded p-3">
                  <p className="text-[#64748B] text-xs mb-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Address
                    {selectedBooking.lat && selectedBooking.lng && (
                      <span className="text-green-600 ml-auto text-[10px]">
                        ✓ Located
                      </span>
                    )}
                  </p>
                  <p className="text-[#111111]">{getBookingAddress(selectedBooking) || 'No address'}</p>
                </div>

                {/* Client Company */}
                {selectedBooking.client_name && (
                  <div className="flex items-center gap-2 px-1">
                    <span className="text-[10px] font-semibold uppercase text-[#94A3B8] tracking-wide">Client</span>
                    <span className="text-xs font-medium text-[#0D9488]">{selectedBooking.client_name}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-[#F8FAFC] rounded p-3">
                    <p className="text-[#64748B] text-xs mb-1 flex items-center gap-1">
                      <User className="w-3 h-3" /> Customer
                    </p>
                    <p className="text-[#111111] text-sm">{selectedBooking.waste_details?.customer_name}</p>
                    {selectedBooking.waste_details?.customer_mobile ? (
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-[#64748B] text-xs flex-1">{selectedBooking.waste_details.customer_mobile}</p>
                        <a
                          href={`tel:${selectedBooking.waste_details.customer_mobile}`}
                          className="p-1.5 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                          title="Call customer"
                          data-testid="call-customer-btn"
                        >
                          <Phone className="w-3.5 h-3.5" />
                        </a>
                        <button
                          type="button"
                          onClick={() => setSmsModalOpen(true)}
                          className="p-1.5 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                          title="Send SMS"
                          data-testid="sms-customer-btn"
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <p className="text-[#64748B] text-xs">No mobile</p>
                    )}
                  </div>
                  <div className="bg-[#F8FAFC] rounded p-3">
                    <p className="text-[#64748B] text-xs mb-1 flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Schedule
                    </p>
                    <p className="text-[#111111] text-sm">{selectedBooking.scheduled_date || 'Not set'}</p>
                    <p className="text-[#64748B] text-xs">{selectedBooking.scheduled_time_window || '-'}</p>
                  </div>
                </div>

                {selectedBooking.ops_notes && (
                  <div className="bg-yellow-50 rounded p-3 border border-yellow-200">
                    <p className="text-[#64748B] text-xs mb-1">Notes</p>
                    <p className="text-[#111111] text-sm">{selectedBooking.ops_notes}</p>
                  </div>
                )}
              </div>

              {/* Driver Assignment */}
              <div className="bg-[#F8FAFC] rounded p-3 mb-4">
                <p className="text-[#64748B] text-xs mb-2 font-semibold uppercase flex items-center gap-1">
                  <Truck className="w-3 h-3" /> Assigned Driver
                </p>
                <select
                  value={selectedBooking.assigned_driver_id || ''}
                  onChange={(e) => assignDriver(selectedBooking.id, e.target.value)}
                  disabled={assigningDriver}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="assign-driver-select"
                >
                  <option value="">-- Unassigned --</option>
                  {availableDrivers.filter(d => d.status !== 'offline').map(d => (
                    <option key={d.id} value={d.id}>
                      {d.name}{d.vehicle_type ? ` (${d.vehicle_type})` : ''}{d.status === 'on_job' ? ' [On Job]' : ''}
                    </option>
                  ))}
                </select>
                {selectedBooking.assigned_driver_name && (
                  <p className="text-xs text-[#0D9488] mt-1 font-medium">
                    Currently: {selectedBooking.assigned_driver_name}
                  </p>
                )}
              </div>

              {/* Quick Actions */}
              <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                <Button
                  className="w-full mb-3 bg-[#0D9488] hover:bg-[#0F766E]"
                  onClick={() => window.open(`/admin/bookings?bookingId=${selectedBooking.id}`, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Full Booking
                </Button>

                {/* Quick Status Change */}
                <div>
                  <p className="text-xs font-semibold text-[#64748B] uppercase mb-2">Quick Status</p>
                  <Input
                    placeholder="Note (required for some statuses)"
                    value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)}
                    className="text-sm mb-2"
                  />
                  <div className="flex flex-wrap gap-1">
                    {['confirmed', 'on_site', 'collected', 'completed'].map(status => (
                      <Button
                        key={status}
                        size="sm"
                        variant="outline"
                        className="text-xs"
                        style={{ 
                          borderColor: INTERNAL_STATUS_COLORS[status],
                          color: INTERNAL_STATUS_COLORS[status]
                        }}
                        onClick={() => updateInternalStatus(selectedBooking.id, status)}
                        disabled={isUpdating || selectedBooking.internal_status === status}
                      >
                        {statusOptions?.internal_statuses?.[status]?.label || status}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Route Planner Panel */}
        {routeMode && (
          <RoutePlanner
            selectedStops={routeStops}
            onRemoveStop={removeRouteStop}
            onReorderStops={reorderRouteStops}
            onClose={toggleRouteMode}
            onClearStops={clearRouteStops}
            drivers={availableDrivers}
          />
        )}

        {/* Send SMS Modal */}
        {smsModalOpen && selectedBooking && (
          <SendSMSModal
            booking={selectedBooking}
            defaultTo={selectedBooking.waste_details?.customer_mobile || ''}
            onClose={() => setSmsModalOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default MapDispatch;
